import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import './HomePage.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── SVG helpers ──────────────────────────────────────────────────
const ClockSvg = ({ size = 16, className = '' }) => (
  <svg viewBox="0 0 16 16" fill="none" stroke="#22c77a" strokeWidth="1.5" strokeLinecap="round" width={size} height={size} className={className}>
    <circle cx="8" cy="8" r="6.5" />
    <path d="M8 4.5V8l2.5 1.5" />
  </svg>
);

// ─── TERMS content ────────────────────────────────────────────────
const TermsContent = () => (
  <div className="hp-legal-scroll">
    <p><strong>Effective date:</strong> 3 October 2025</p>
    <p><strong>Company:</strong> Tyme ("we", "us", "our")</p>
    <p><strong>Contact:</strong> tymeeltd@gmail.com</p>
    <p>By accessing or using Tyme's website and services, you agree to the following Terms &amp; Conditions. If you do not agree, you must not use our services.</p>
    <h3>1. Use of Service</h3>
    <p>Tyme provides cloud-based staff attendance and time-tracking tools. You agree to use the service only for lawful purposes and in accordance with these terms.</p>
    <p>You are responsible for maintaining the confidentiality of your account login details and for all activities under your account.</p>
    <h3>2. Free Trial and Subscription</h3>
    <ul>
      <li>We offer a 3-day free trial on the Pro Plan.</li>
      <li>After the trial, your subscription will automatically convert to a paid monthly plan unless you cancel before the trial period ends.</li>
      <li>You can cancel your subscription at any time from your account dashboard.</li>
      <li>Subscription fees are charged monthly in advance and are non-refundable once the billing period starts.</li>
    </ul>
    <h3>3. Payment and Billing</h3>
    <ul>
      <li>All payments are processed securely through our payment provider.</li>
      <li>You are responsible for providing accurate billing information and ensuring sufficient funds.</li>
      <li>We reserve the right to suspend or terminate your account if payment fails.</li>
    </ul>
    <h3>4. Intellectual Property</h3>
    <p>All content, software, logos, and materials provided by Tyme are owned by us or licensed to us. You may not copy, distribute, modify, or use them for commercial purposes without our prior written consent.</p>
    <h3>5. Service Availability</h3>
    <p>We aim to provide uninterrupted access to our services, but we do not guarantee that the platform will be error-free or available 100% of the time. We may temporarily suspend services for maintenance or updates.</p>
    <h3>6. Limitation of Liability</h3>
    <p>To the fullest extent permitted by law, Tyme is not liable for:</p>
    <ul>
      <li>Any indirect, incidental, or consequential damages arising from the use of our services.</li>
      <li>Any data loss, unauthorised access, or service interruption beyond our reasonable control.</li>
    </ul>
    <h3>7. Use of Artificial Intelligence (AI)</h3>
    <p>Tyme incorporates AI-powered tools and features to support and enhance the functionality of our services. These may include automated analytics, recommendations, and other forms of intelligent assistance.</p>
    <p>By using Tyme, you acknowledge and agree that:</p>
    <ul>
      <li>AI-powered outputs are generated based on available data and algorithms and are provided for informational and operational purposes only.</li>
      <li>Tyme does not guarantee the accuracy, completeness, or suitability of any AI-generated suggestions or insights.</li>
      <li>You remain solely responsible for any decisions or actions taken based on AI-assisted outputs.</li>
    </ul>
    <p>We are committed to ensuring that any AI features are deployed ethically, securely, and in compliance with relevant data-protection and privacy laws.</p>
    <h3>8. Termination</h3>
    <p>We reserve the right to suspend or terminate your account if you violate these Terms &amp; Conditions or misuse our services.</p>
    <h3>9. Changes to These Terms</h3>
    <p>We may update these Terms from time to time. Continued use of Tyme after changes means you accept the revised terms.</p>
    <h3>10. Governing Law</h3>
    <p>These Terms &amp; Conditions are governed by and construed in accordance with the laws of England and Wales, and any disputes shall be subject to the exclusive jurisdiction of UK courts.</p>
    <div className="hp-legal-contact">
      <p><strong>Contact Us:</strong></p>
      <p>For questions about these Terms or our Privacy Policy, email us at: <a href="mailto:tymeeltd@gmail.com">tymeeltd@gmail.com</a></p>
    </div>
  </div>
);

// ─── PRIVACY content ──────────────────────────────────────────────
const PrivacyContent = () => (
  <div className="hp-legal-scroll">
    <p><strong>Effective date:</strong> 3 October 2025</p>
    <p><strong>Company:</strong> Tyme ("we", "us", "our")</p>
    <p><strong>Contact:</strong> tymeeltd@gmail.com</p>
    <p>At Tyme, we are committed to protecting your privacy and handling your personal data responsibly and transparently. This Privacy Policy explains how we collect, use, store, and protect your information when you use our website and services.</p>
    <h3>1. Information We Collect</h3>
    <p>We collect and process the following types of personal data:</p>
    <ul>
      <li>Email addresses – when you sign up for an account or subscribe to our services.</li>
      <li>Device and cookie data – collected automatically when you visit our website to help us improve performance, security, and user experience.</li>
      <li>Payment details – processed securely via our payment partner when you subscribe to a paid plan.</li>
    </ul>
    <p>We do not collect location data, PIN codes, passwords, or unnecessary personal information.</p>
    <h3>2. How We Use Your Data</h3>
    <p>We use the information we collect for the following purposes:</p>
    <ul>
      <li>To create and manage your Tyme account.</li>
      <li>To provide and maintain our staff attendance and time-tracking services.</li>
      <li>To communicate with you about updates, support, and changes to our services.</li>
      <li>To process payments and manage your subscription.</li>
      <li>To improve and optimise our website and platform functionality.</li>
    </ul>
    <p>We will never sell or share your data with third parties for marketing purposes.</p>
    <h3>3. Legal Basis for Processing</h3>
    <p>We process your personal data under the following legal grounds:</p>
    <ul>
      <li>Contract: to provide the service you signed up for.</li>
      <li>Consent: when you agree to receive optional marketing emails.</li>
      <li>Legitimate interests: for service improvement and security.</li>
    </ul>
    <h3>4. Cookies</h3>
    <p>Tyme uses cookies and similar technologies to:</p>
    <ul>
      <li>Analyse site traffic and usage.</li>
      <li>Enhance site functionality.</li>
      <li>Personalise your experience.</li>
    </ul>
    <p>You can manage or disable cookies in your browser settings, but some features may not work properly if you do so.</p>
    <h3>5. Data Retention</h3>
    <p>We keep your personal data only as long as necessary:</p>
    <ul>
      <li>Account data is retained while your account is active.</li>
      <li>If you close your account, we will delete or anonymise your data within 30 days unless required to keep it by law.</li>
    </ul>
    <h3>6. Your Rights</h3>
    <p>Under UK GDPR, you have the right to:</p>
    <ul>
      <li>Access the personal data we hold about you.</li>
      <li>Request correction or deletion of your data.</li>
      <li>Withdraw consent for marketing.</li>
      <li>Request data portability.</li>
      <li>Lodge a complaint with the ICO (Information Commissioner's Office).</li>
    </ul>
    <p>To exercise these rights, contact us at tymeeltd@gmail.com.</p>
    <h3>7. Security</h3>
    <p>We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, loss, misuse, or disclosure.</p>
    <h3>8. Use of Artificial Intelligence (AI)</h3>
    <p>Tyme uses artificial intelligence (AI) technologies within certain parts of our platform to enhance functionality and improve user experience. These features may include, but are not limited to, automated data insights, predictive analytics, natural-language processing, and smart notifications designed to optimise staff attendance tracking and reporting.</p>
    <p>We use AI strictly to improve the efficiency, accuracy, and automation of our services. AI models may process data you provide (such as attendance records, usage patterns, or account preferences) to deliver features like smart scheduling recommendations or automatic summaries.</p>
    <ul>
      <li>No personal data is sold, shared, or used to train external AI models.</li>
      <li>All AI processing complies with UK GDPR, and your data remains protected by the same security standards applied elsewhere in our platform.</li>
      <li>Any data used for internal AI model training or service improvement is anonymised or aggregated wherever possible.</li>
    </ul>
    <p>By using Tyme, you consent to the use of AI in accordance with this Privacy Policy.</p>
    <h3>9. Changes to This Policy</h3>
    <p>We may update this Privacy Policy from time to time. Any changes will be posted on this page with a new "effective date".</p>
    <div className="hp-legal-contact">
      <p><strong>Contact Us:</strong></p>
      <p>For questions about our Privacy Policy, email us at: <a href="mailto:tymeeltd@gmail.com">tymeeltd@gmail.com</a></p>
    </div>
  </div>
);

// ─── Ticker data ──────────────────────────────────────────────────
const tickerItems = [
  { color: '#22c77a', text: 'Sarah K. clocked in \u00b7 08:52' },
  { color: '#e85252', text: 'Dan T. late arrival \u00b7 09:14' },
  { color: '#22c77a', text: 'James H. clocked in \u00b7 08:31' },
  { color: '#9b78e8', text: 'Weekly hours report sent \u00b7 Mon 07:00' },
  { color: '#22c77a', text: 'Maya R. clocked in \u00b7 08:44' },
  { color: '#e8a320', text: 'Tom B. clocked out \u00b7 17:02' },
  { color: '#22c77a', text: 'Priya S. clocked in \u00b7 08:59' },
  { color: '#9b78e8', text: 'Monthly report emailed \u00b7 1st Mar' },
  { color: '#e85252', text: 'Chris M. late arrival \u00b7 09:22' },
  { color: '#22c77a', text: 'Aisha L. clocked in \u00b7 08:48' },
  { color: '#e8a320', text: 'Ravi P. clocked out \u00b7 17:30' },
  { color: '#22c77a', text: 'Emma W. clocked in \u00b7 08:55' },
];

// ─── Validation helpers ───────────────────────────────────────────
const validateEmail = (email) => {
  if (!email) return 'Email is required';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Please enter a valid email address';
  return '';
};
const validateFullName = (name) => {
  if (!name) return 'Full name is required';
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return 'Please enter your full name (first and last name)';
  if (parts.some(p => p.length < 2)) return 'Each name must be at least 2 characters long';
  return '';
};
const validatePassword = (pw) => {
  const errors = [];
  if (pw.length < 8) errors.push('At least 8 characters long');
  if (!/(?=.*[a-z])/.test(pw)) errors.push('At least one lowercase letter');
  if (!/(?=.*[A-Z])/.test(pw)) errors.push('At least one uppercase letter');
  if (!/(?=.*\d)/.test(pw)) errors.push('At least one number');
  if (!/(?=.*[@$!%*?&])/.test(pw)) errors.push('At least one special character (@$!%*?&)');
  return errors;
};

// =====================================================================
// MAIN COMPONENT
// =====================================================================
const HomePage = () => {
  const navigate = useNavigate();

  // Modal states
  const [showSignin, setShowSignin] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showForgotPw, setShowForgotPw] = useState(false);
  const [forgotStep, setForgotStep] = useState('email'); // email | code

  // Form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPw, setSignupPw] = useState('');
  const [signupConfirm, setSignupConfirm] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyEmail, setVerifyEmail] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [forgotNewPw, setForgotNewPw] = useState('');
  const [forgotConfirmPw, setForgotConfirmPw] = useState('');

  const [loading, setLoading] = useState(false);
  const [pwErrors, setPwErrors] = useState([]);
  const [formError, setFormError] = useState('');

  // If already logged in, show dashboard button
  const isLoggedIn = !!localStorage.getItem('tyme_session');

  // Escape key
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        setShowSignin(false); setShowCreate(false); setShowVerify(false);
        setShowTerms(false); setShowPrivacy(false); setShowForgotPw(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // ─── AUTH HANDLERS ───────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setFormError('');
    setLoading(true);
    try {
      // Manager shortcut
      if (!loginEmail.includes('@') && loginEmail.toLowerCase() === 'sibel') {
        try {
          const r = await axios.post(`${API}/manager/login`, { username: loginEmail, password: loginPassword });
          if (r.data.success) {
            window.loginTime = Date.now();
            localStorage.setItem('tyme_session', r.data.session_id);
            localStorage.setItem('tyme_user', JSON.stringify({ type: 'manager', username: loginEmail }));
            toast.success('Manager login successful');
            navigate('/manager-dashboard');
            return;
          }
        } catch (_) { /* fall through */ }
      }
      const r = await axios.post(`${API}/auth/login`, { email_or_name: loginEmail, password: loginPassword });
      if (r.data.session_id) {
        window.loginTime = Date.now();
        localStorage.setItem('tyme_session', r.data.session_id);
        localStorage.setItem('tyme_user', JSON.stringify({ type: r.data.user?.subscription_status === 'admin' ? 'admin' : 'user', ...r.data.user }));
        toast.success(r.data.message || 'Login successful');
        navigate('/manager-dashboard');
      }
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!acceptTerms || !acceptPrivacy) { setFormError('You must accept both the Terms & Conditions and Privacy Policy'); return; }
    const emailErr = validateEmail(signupEmail);
    if (emailErr) { setFormError(emailErr); return; }
    const nameErr = validateFullName(signupName);
    if (nameErr) { setFormError(nameErr); return; }
    const pwErr = validatePassword(signupPw);
    if (pwErr.length) { setPwErrors(pwErr); return; }
    if (signupPw !== signupConfirm) { setFormError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/signup`, { name: signupName, email: signupEmail, password: signupPw, confirm_password: signupConfirm });
      setVerifyEmail(signupEmail);
      setVerifyCode('');
      setShowCreate(false);
      setShowVerify(true);
      toast.success(r.data.message || 'Verification code sent to your email!');
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setFormError('');
    setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/verify-email`, { email: verifyEmail, verification_code: verifyCode });
      if (r.data.session_id) {
        localStorage.setItem('tyme_session', r.data.session_id);
        localStorage.setItem('tyme_user', JSON.stringify({ email: verifyEmail, name: r.data.user?.name || signupName }));
        setShowVerify(false);
        toast.success('Account created successfully! Welcome to Tyme');
        navigate('/manager-dashboard');
      }
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await axios.post(`${API}/auth/resend-code`, { email: verifyEmail });
      toast.success('Verification code resent!');
    } catch (_) {
      toast.error('Failed to resend code');
    }
  };

  const handleForgotSend = async (e) => {
    e.preventDefault();
    setFormError('');
    const emailErr = validateEmail(forgotEmail);
    if (emailErr) { setFormError(emailErr); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.toLowerCase() });
      setForgotStep('code');
      toast.success('If an account exists, a verification code has been sent.');
    } catch (_) {
      setForgotStep('code');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotReset = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!forgotCode || forgotCode.length !== 6) { setFormError('Please enter a valid 6-digit code.'); return; }
    const pwErr = validatePassword(forgotNewPw);
    if (pwErr.length) { setFormError('Password must meet all requirements.'); return; }
    if (forgotNewPw !== forgotConfirmPw) { setFormError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { email: forgotEmail.toLowerCase(), verification_code: forgotCode, new_password: forgotNewPw });
      toast.success('Password reset successfully! Please log in.');
      setShowForgotPw(false);
      setForgotStep('email');
      setForgotEmail(''); setForgotCode(''); setForgotNewPw(''); setForgotConfirmPw('');
      setShowSignin(true);
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Invalid or expired code.');
    } finally {
      setLoading(false);
    }
  };

  // helpers
  const openSignin = () => { setShowCreate(false); setShowVerify(false); setFormError(''); setPwErrors([]); setShowSignin(true); };
  const openCreate = () => { setShowSignin(false); setFormError(''); setPwErrors([]); setShowCreate(true); };
  const openForgotPw = () => { setShowSignin(false); setFormError(''); setForgotStep('email'); setShowForgotPw(true); };
  const scrollTo = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); };
  const wobbleGrowth = () => {
    const card = document.getElementById('growth-plan');
    if (!card) return;
    card.classList.remove('wobble');
    void card.offsetWidth;
    card.classList.add('wobble');
    setTimeout(() => card.classList.remove('wobble'), 700);
  };

  // ─── RENDER ──────────────────────────────────────────────────
  return (
    <div className="hp-root" data-testid="home-page">

      {/* ── NAV ── */}
      <nav className="hp-nav" data-testid="nav-bar">
        <div className="hp-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <div className="hp-logo-icon"><ClockSvg /></div>
          <span className="hp-logo-text">Tyme</span>
        </div>
        <ul className="hp-nav-links">
          <li><a onClick={() => scrollTo('features')}>Features</a></li>
          <li><a onClick={() => scrollTo('how')}>How it works</a></li>
          <li><a onClick={() => scrollTo('plans')}>Pricing</a></li>
        </ul>
        <div className="hp-nav-right">
          {isLoggedIn ? (
            <button className="hp-btn-green" data-testid="nav-dashboard-btn" onClick={() => navigate('/manager-dashboard')}>Dashboard</button>
          ) : (
            <>
              <button className="hp-btn-ghost-sm" data-testid="nav-signin-btn" onClick={openSignin}>Sign in</button>
              <button className="hp-btn-green" data-testid="nav-start-free-btn" onClick={() => { scrollTo('plans'); wobbleGrowth(); }}>Start free trial →</button>
            </>
          )}
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="hp-hero">
        <div className="hp-hero-left">
          <div className="hp-hero-badge"><span className="hp-badge-dot" />{' '}UK Time & Attendance Platform</div>
          <h1 className="hp-hero-h1">Your workforce,<br /><span className="accent">always in view</span></h1>
          <p className="hp-hero-sub">Real-time check-ins, leave management, rota building, and payroll exports — all from one control panel your managers will actually use every day.</p>
          <p className="hp-hero-tagline">You manage the business. We'll handle the hours.</p>
          <div className="hp-hero-actions">
            <button className="hp-btn-primary-lg" data-testid="hero-start-trial-btn" onClick={() => { scrollTo('plans'); wobbleGrowth(); }}>Start 3-day free trial</button>
          </div>
          <div className="hp-hero-meta">
            <span className="hp-hero-meta-item">No credit card needed</span>
            <span className="hp-hero-meta-item">5-min setup</span>
          </div>
        </div>

        <div className="hp-hero-right">
          <div className="hp-app-mock">
            {/* Top bar */}
            <div className="hp-mock-topbar">
              <div className="hp-mock-brand">
                <div className="hp-mock-brand-icon"><ClockSvg size={13} /></div>
                Tyme Control Panel
              </div>
              <div className="hp-mock-pill">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.2"><rect x="1" y="1" width="8" height="8" rx="1.5" /></svg>
                Business Plan
              </div>
            </div>
            {/* Body */}
            <div className="hp-mock-body">
              {/* Sidebar */}
              <div className="hp-mock-sidebar">
                <div className="hp-mock-nav-item active">
                  <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="1" y="1" width="5" height="5" rx="1" /><rect x="8" y="1" width="5" height="5" rx="1" /><rect x="1" y="8" width="5" height="5" rx="1" /><rect x="8" y="8" width="5" height="5" rx="1" /></svg>
                  Live Status
                </div>
                <div className="hp-mock-nav-item">
                  <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="7" cy="4.5" r="2.5" /><path d="M1.5 12c0-2.5 2.5-4 5.5-4s5.5 1.5 5.5 4" /></svg>
                  Employees
                </div>
                <div className="hp-mock-nav-item">
                  <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="7" cy="7" r="5.5" /><path d="M7 3.5V7l2 1.2" /></svg>
                  Hours
                </div>
                <div className="hp-mock-nav-item">
                  <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="4.5" cy="4.5" r="1" /><path d="M7 5h5M2 9h10" /></svg>
                  Attendance Rules
                </div>
                <div className="hp-mock-nav-sep" />
                <div className="hp-mock-nav-item">
                  <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="2" y="1" width="10" height="12" rx="1.5" /><path d="M4 5h6M4 8h4" /></svg>
                  Rota Builder
                </div>
                <div className="hp-mock-logout">
                  <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"><path d="M8 6H2m0 0l2-2M2 6l2 2M5 2H10a.5.5 0 01.5.5v7A.5.5 0 0110 10H5" /></svg>
                  Logout
                </div>
              </div>
              {/* Main */}
              <div className="hp-mock-main">
                <div className="hp-mock-stats">
                  <div className="hp-mock-stat"><div className="hp-mock-stat-label">Checked In</div><div className="hp-mock-stat-val" style={{ color: 'var(--stat-green)' }}>12</div></div>
                  <div className="hp-mock-stat"><div className="hp-mock-stat-label">On Break</div><div className="hp-mock-stat-val" style={{ color: 'var(--stat-yellow)' }}>3</div></div>
                  <div className="hp-mock-stat"><div className="hp-mock-stat-label">Today Total</div><div className="hp-mock-stat-val" style={{ color: 'var(--stat-purple)' }}>18</div></div>
                  <div className="hp-mock-stat"><div className="hp-mock-stat-label">Open Sessions</div><div className="hp-mock-stat-val" style={{ color: 'var(--stat-red)' }}>2</div></div>
                </div>
                <div className="hp-mock-card">
                  <div className="hp-mock-card-head">Currently Checked In</div>
                  <div className="hp-mock-row">
                    <div className="hp-mock-avatar" style={{ background: 'rgba(34,199,122,0.15)', color: '#22c77a' }}>SK</div>
                    <span className="hp-mock-emp-name">Sarah K.</span>
                    <span className="hp-mock-time">08:52</span>
                    <span className="hp-mock-badge hp-mb-in">In</span>
                  </div>
                  <div className="hp-mock-row">
                    <div className="hp-mock-avatar" style={{ background: 'rgba(232,82,82,0.15)', color: '#e85252' }}>DT</div>
                    <span className="hp-mock-emp-name">Dan T.</span>
                    <span className="hp-mock-time">09:14</span>
                    <span className="hp-mock-badge hp-mb-late">Late</span>
                  </div>
                  <div className="hp-mock-row">
                    <div className="hp-mock-avatar" style={{ background: 'rgba(34,199,122,0.15)', color: '#22c77a' }}>JH</div>
                    <span className="hp-mock-emp-name">James H.</span>
                    <span className="hp-mock-time">08:31</span>
                    <span className="hp-mock-badge hp-mb-in">In</span>
                  </div>
                  <div className="hp-mock-row">
                    <div className="hp-mock-avatar" style={{ background: 'rgba(155,120,232,0.15)', color: '#9b78e8' }}>MR</div>
                    <span className="hp-mock-emp-name">Maya R.</span>
                    <span className="hp-mock-time">—</span>
                    <span className="hp-mock-badge hp-mb-out">Leave</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="hp-features-wrap" id="features">
        <div className="hp-section-eyebrow">capabilities</div>
        <h2 className="hp-section-h2">Built for managers who <span className="dim">need answers fast</span></h2>
        <p className="hp-section-sub">Everything in one control panel — no switching between apps, no spreadsheets, no guesswork.</p>
        <div className="hp-feat-grid">
          <div className="hp-feat"><div className="hp-feat-num">01</div><div className="hp-feat-icon-wrap"><svg viewBox="0 0 19 19"><circle cx="9.5" cy="9.5" r="7.5" /><path d="M9.5 5.5V9.5l3 1.8" /></svg></div><div className="hp-feat-title">Live status dashboard</div><div className="hp-feat-desc">See who's in, on break, late, or absent right now. Four stat cards, always up to date — no refresh needed.</div></div>
          <div className="hp-feat"><div className="hp-feat-num">02</div><div className="hp-feat-icon-wrap"><svg viewBox="0 0 19 19"><rect x="2.5" y="3.5" width="14" height="13" rx="2" /><path d="M13 2v3M6 2v3M2.5 9h14" /></svg></div><div className="hp-feat-title">Leave &amp; absence management</div><div className="hp-feat-desc">Employees request time off in-app. Approve or decline in one tap. Holiday balances, sick days, TOIL — all tracked automatically.</div></div>
          <div className="hp-feat"><div className="hp-feat-num">03</div><div className="hp-feat-icon-wrap"><svg viewBox="0 0 19 19"><rect x="2" y="4" width="15" height="12" rx="2" /><path d="M5 4V2.5M14 4V2.5M2 8h15" /></svg></div><div className="hp-feat-title">Rota builder</div><div className="hp-feat-desc">Drag-and-drop scheduling for every shift. Set recurring patterns, publish to staff instantly, and spot coverage gaps before they happen.</div></div>
          <div className="hp-feat"><div className="hp-feat-num">04</div><div className="hp-feat-icon-wrap"><svg viewBox="0 0 19 19"><circle cx="9.5" cy="9.5" r="4" /><path d="M9.5 2.5v3M9.5 13.5v3M2.5 9.5h3M13.5 9.5h3" /></svg></div><div className="hp-feat-title">Attendance rules engine</div><div className="hp-feat-desc">Define early check-in windows, break deductions, and overtime thresholds. Rule violations notify managers automatically.</div></div>
          <div className="hp-feat"><div className="hp-feat-num">05</div><div className="hp-feat-icon-wrap"><svg viewBox="0 0 19 19"><path d="M5 15V8M9.5 15V4M14 15v-5" /></svg></div><div className="hp-feat-title">Weekly hours overview</div><div className="hp-feat-desc">Per-employee hours at a glance — this week, last week, this month. Filter by team, export to payroll in one click.</div></div>
          <div className="hp-feat"><div className="hp-feat-num">06</div><div className="hp-feat-icon-wrap"><svg viewBox="0 0 19 19"><rect x="3.5" y="2.5" width="12" height="14" rx="2" /><path d="M6.5 7h6M6.5 10h6M6.5 13h4" /></svg></div><div className="hp-feat-title">Payroll-ready exports</div><div className="hp-feat-desc">Export approved hours as a clean report your payroll process can actually use. No re-keying, no errors, no late payroll runs.</div></div>
          <div className="hp-feat hp-feat-full">
            <div><div className="hp-feat-num">07</div><div className="hp-feat-icon-wrap"><svg viewBox="0 0 19 19" fill="none" stroke="#22c77a" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="15" height="11" rx="2" /><path d="M2 7l7.5 5 7.5-5" /></svg></div></div>
            <div><div className="hp-feat-title" style={{ marginTop: 0 }}>Automated staff hours emails</div><div className="hp-feat-desc">Staff hours are automatically emailed to you every week or every month — so you always have a full record without logging in.</div></div>
          </div>
        </div>
      </section>

      {/* ── TICKER ── */}
      <div className="hp-ticker-wrap">
        <div className="hp-ticker-track">
          <div className="hp-ticker-set">
            {tickerItems.map((t, i) => (
              <span className="hp-tick" key={i}><span className="hp-tick-dot" style={{ background: t.color }} />{t.text}</span>
            ))}
          </div>
          <div className="hp-ticker-set" aria-hidden="true">
            {tickerItems.map((t, i) => (
              <span className="hp-tick" key={`dup-${i}`}><span className="hp-tick-dot" style={{ background: t.color }} />{t.text}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ── WHY TYME ── */}
      <section className="hp-why-section">
        <div className="hp-section-eyebrow">why tyme</div>
        <h2 className="hp-section-h2">Running a team is hard enough —<br /><span className="dim">tracking hours shouldn't be.</span></h2>
        <div className="hp-why-grid">
          <div className="hp-why-card">
            <div className="hp-why-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
            </div>
            <h3 className="hp-why-title">Set it up once</h3>
            <p className="hp-why-desc">Let it run your staff hours for you. No daily admin, no chasing updates — it just works.</p>
          </div>
          <div className="hp-why-card">
            <div className="hp-why-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            </div>
            <h3 className="hp-why-title">Everything in one place</h3>
            <p className="hp-why-desc">No more WhatsApp groups, paper sheets, or chasing staff. Stop wasting time managing time.</p>
          </div>
          <div className="hp-why-card">
            <div className="hp-why-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
            </div>
            <h3 className="hp-why-title">Know where your money's going</h3>
            <p className="hp-why-desc">See labour costs per shift, per week, per team. No more payroll surprises.</p>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="hp-section" id="how">
        <div className="hp-section-eyebrow">getting started</div>
        <h2 className="hp-section-h2">Up and running <span className="dim">in one afternoon</span></h2>
        <div className="hp-how-grid">
          <div className="hp-how-step"><div className="hp-how-num">01</div><div className="hp-how-title">Create your account</div><div className="hp-how-desc">Sign up, add your employees to the control panel, and assign each one a unique PIN.</div></div>
          <div className="hp-how-step"><div className="hp-how-num">02</div><div className="hp-how-title">Configure your rules</div><div className="hp-how-desc">Set workday hours, break deductions, check-in windows, and late alerts per team.</div></div>
          <div className="hp-how-step"><div className="hp-how-num">03</div><div className="hp-how-title">Invite your team</div><div className="hp-how-desc">Staff get a PIN and clock in from mobile, tablet kiosk, or browser — no training needed.</div></div>
          <div className="hp-how-step"><div className="hp-how-num">04</div><div className="hp-how-title">Approve &amp; export</div><div className="hp-how-desc">Review timesheets, approve in one click, push to payroll. Payroll day now takes minutes not hours.</div></div>
        </div>
      </section>

      {/* ── PLANS ── */}
      <section className="hp-section" id="plans">
        <div className="hp-section-eyebrow">pricing</div>
        <h2 className="hp-section-h2">Simple plans, <span className="dim">no surprises</span></h2>
        <p className="hp-pricing-tagline">No per-employee fees. One simple monthly price.<br /><span className="hp-pricing-sub">Fixed pricing that doesn't punish you for growing your team.</span></p>
        <div className="hp-plans-grid">
          <div className="hp-plan">
            <div className="hp-plan-name">Starter</div>
            <div className="hp-plan-price"><sup>£</sup>49<span className="per"> / month</span></div>
            <p className="hp-plan-desc">For small teams getting started with digital time tracking and attendance management.</p>
            <ul className="hp-plan-list"><li>Up to 15 staff</li><li>PIN-based clock-in</li><li>Live attendance dashboard</li><li>Leave management</li><li>Hours reporting</li></ul>
            <button className="hp-plan-btn" data-testid="plan-starter-btn" onClick={openCreate}>Get started</button>
          </div>
          <div className="hp-plan featured" id="growth-plan">
            <div className="hp-plan-name">Growth</div>
            <div className="hp-plan-price"><sup>£</sup>69<span className="per"> / month</span></div>
            <p className="hp-plan-desc">For growing businesses with larger teams and more complex scheduling needs.</p>
            <ul className="hp-plan-list"><li>Up to 30 staff</li><li>Everything in Starter</li><li>Rota builder</li><li>Hours overview &amp; reporting</li><li>Multi-location support</li></ul>
            <button className="hp-plan-btn" data-testid="plan-growth-btn" onClick={openCreate}>Start free trial</button>
          </div>
          <div className="hp-plan">
            <div className="hp-plan-name">Pro</div>
            <div className="hp-plan-price"><sup>£</sup>99<span className="per"> / month</span></div>
            <p className="hp-plan-desc">For larger teams that need full control over attendance, hours, and workforce management.</p>
            <ul className="hp-plan-list"><li>Up to 60 staff</li><li>Everything in Growth</li><li>Advanced attendance rules</li><li>Priority support</li></ul>
            <button className="hp-plan-btn" data-testid="plan-pro-btn" onClick={openCreate}>Get started</button>
          </div>
          <div className="hp-plan">
            <div className="hp-plan-name">Enterprise</div>
            <div className="hp-plan-price">Custom</div>
            <p className="hp-plan-desc">For organisations with 60+ staff or specific requirements. Get a tailored quote.</p>
            <ul className="hp-plan-list"><li>60+ staff</li><li>Everything in Pro</li><li>Dedicated account manager</li></ul>
            <a href="mailto:tymeeltd@gmail.com" className="hp-plan-btn" style={{ borderColor: 'rgba(34,199,122,0.3)', color: 'var(--green)', fontSize: '13px' }}>tymeeltd@gmail.com</a>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <div className="hp-cta-band">
        <div>
          <h2 className="hp-cta-h2">Ready to open your<br />own control panel?</h2>
          <p className="hp-cta-sub">No credit card required. Up and running today.</p>
        </div>
        <div className="hp-cta-right">
          <button className="hp-btn-primary-lg" data-testid="cta-start-trial-btn" onClick={() => { scrollTo('plans'); wobbleGrowth(); }}>Start free trial</button>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer className="hp-footer">
        <div className="hp-footer-brand">
          <div className="hp-footer-logo">
            <div className="hp-footer-logo-icon"><svg viewBox="0 0 14 14" fill="none" stroke="#22c77a" strokeWidth="1.4" strokeLinecap="round"><circle cx="7" cy="7" r="5.5" /><path d="M7 4V7l2 1.2" /></svg></div>
            <span className="hp-footer-logo-text">Tyme</span>
          </div>
          <p className="hp-footer-tagline">UK employee time tracking and attendance management. Built for businesses that value their people's time.</p>
        </div>
        <div className="hp-footer-col">
          <h4>Product</h4>
          <ul>
            <li><a onClick={() => scrollTo('features')}>Features</a></li>
            <li><a onClick={() => scrollTo('plans')}>Pricing</a></li>
          </ul>
        </div>
        <div className="hp-footer-col">
          <h4>Company</h4>
          <ul>
            <li><a href="mailto:tymeeltd@gmail.com">Contact</a></li>
          </ul>
        </div>
        <div className="hp-footer-col">
          <h4>Legal</h4>
          <ul>
            <li><a onClick={() => setShowPrivacy(true)} data-testid="footer-privacy-link">Privacy policy</a></li>
            <li><a onClick={() => setShowTerms(true)} data-testid="footer-terms-link">Terms of service</a></li>
          </ul>
        </div>
      </footer>
      <div className="hp-footer-bottom">
        <span>© 2026 Tyme Ltd. Registered in England &amp; Wales.</span>
        <span>Made in the UK</span>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          MODALS
          ═══════════════════════════════════════════════════════════ */}

      {/* ── SIGN IN ── */}
      <div className={`hp-modal-overlay ${showSignin ? 'open' : ''}`} data-testid="signin-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowSignin(false)}>
        <div className="hp-modal">
          <div className="hp-modal-header">
            <div className="hp-modal-title">Sign in</div>
            <button className="hp-modal-close" data-testid="signin-close-btn" onClick={() => setShowSignin(false)}>✕</button>
          </div>
          <form onSubmit={handleLogin}>
            <div className="hp-modal-fields">
              {formError && showSignin && <div className="hp-error" data-testid="signin-error">{formError}</div>}
              <div className="hp-modal-field">
                <div className="hp-modal-label">Email</div>
                <input className="hp-modal-input" data-testid="signin-email" type="text" placeholder="you@company.com" value={loginEmail} onChange={e => { setLoginEmail(e.target.value); setFormError(''); }} required />
              </div>
              <div className="hp-modal-field">
                <div className="hp-modal-label">Password</div>
                <input className="hp-modal-input" data-testid="signin-password" type="password" placeholder="••••••••" value={loginPassword} onChange={e => { setLoginPassword(e.target.value); setFormError(''); }} required />
              </div>
            </div>
            <button className="hp-modal-submit" data-testid="signin-submit-btn" type="submit" disabled={loading} style={{ marginTop: 14 }}>{loading ? 'Signing in...' : 'Sign in'}</button>
          </form>
          <div style={{ textAlign: 'center' }}>
            <a style={{ fontSize: '12px', color: 'var(--text-3)', cursor: 'pointer' }} onClick={openForgotPw}>Forgot password?</a>
          </div>
          <div className="hp-modal-footer">No account? <a data-testid="signin-to-create-link" onClick={openCreate}>Create one</a></div>
        </div>
      </div>

      {/* ── CREATE ACCOUNT ── */}
      <div className={`hp-modal-overlay ${showCreate ? 'open' : ''}`} data-testid="create-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowCreate(false)}>
        <div className="hp-modal wide">
          <div className="hp-modal-header">
            <div className="hp-modal-title">Create account</div>
            <button className="hp-modal-close" data-testid="create-close-btn" onClick={() => setShowCreate(false)}>✕</button>
          </div>
          <form onSubmit={handleSignup}>
            <div className="hp-modal-fields">
              {formError && showCreate && <div className="hp-error" data-testid="create-error">{formError}</div>}
              <div className="hp-modal-field">
                <div className="hp-modal-label">Full name</div>
                <input className="hp-modal-input" data-testid="create-name" type="text" placeholder="Jane Smith" value={signupName} onChange={e => { setSignupName(e.target.value); setFormError(''); }} required />
              </div>
              <div className="hp-modal-field">
                <div className="hp-modal-label">Email</div>
                <input className="hp-modal-input" data-testid="create-email" type="email" placeholder="you@company.com" value={signupEmail} onChange={e => { setSignupEmail(e.target.value); setFormError(''); }} required />
              </div>
              <div className="hp-modal-field">
                <div className="hp-modal-label">Password</div>
                <input className="hp-modal-input" data-testid="create-password" type="password" placeholder="••••••••" value={signupPw} onChange={e => { setSignupPw(e.target.value); setPwErrors(validatePassword(e.target.value)); setFormError(''); }} required />
                {pwErrors.length > 0 && <div className="hp-pw-errors">{pwErrors.map((err, i) => <span key={i}>• {err}</span>)}</div>}
              </div>
              <div className="hp-modal-field">
                <div className="hp-modal-label">Confirm password</div>
                <input className="hp-modal-input" data-testid="create-confirm-password" type="password" placeholder="••••••••" value={signupConfirm} onChange={e => { setSignupConfirm(e.target.value); setFormError(''); }} required />
              </div>
              {/* Terms & Privacy checkboxes */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                <label className="hp-checkbox-row">
                  <input type="checkbox" checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)} data-testid="create-accept-terms" />
                  <span>I agree to the <a onClick={(e) => { e.preventDefault(); setShowTerms(true); }}>Terms &amp; Conditions</a></span>
                </label>
                <label className="hp-checkbox-row">
                  <input type="checkbox" checked={acceptPrivacy} onChange={e => setAcceptPrivacy(e.target.checked)} data-testid="create-accept-privacy" />
                  <span>I agree to the <a onClick={(e) => { e.preventDefault(); setShowPrivacy(true); }}>Privacy Policy</a></span>
                </label>
              </div>
            </div>
            <button className="hp-modal-submit" data-testid="create-submit-btn" type="submit" disabled={loading || !acceptTerms || !acceptPrivacy} style={{ marginTop: 14, opacity: (!acceptTerms || !acceptPrivacy) ? 0.45 : 1, cursor: (!acceptTerms || !acceptPrivacy) ? 'not-allowed' : 'pointer' }}>{loading ? 'Creating account...' : 'Create account'}</button>
          </form>
          <div className="hp-modal-footer">Already have an account? <a data-testid="create-to-signin-link" onClick={openSignin}>Sign in</a></div>
        </div>
      </div>

      {/* ── EMAIL VERIFICATION ── */}
      <div className={`hp-modal-overlay ${showVerify ? 'open' : ''}`} data-testid="verify-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowVerify(false)}>
        <div className="hp-modal">
          <div className="hp-modal-header">
            <div className="hp-modal-title">Verify email</div>
            <button className="hp-modal-close" onClick={() => setShowVerify(false)}>✕</button>
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-2)', textAlign: 'center' }}>
            We sent a 6-digit code to<br /><strong style={{ color: 'var(--text)' }}>{verifyEmail}</strong>
          </div>
          <form onSubmit={handleVerify}>
            <div className="hp-modal-fields">
              {formError && showVerify && <div className="hp-error">{formError}</div>}
              <div className="hp-modal-field">
                <div className="hp-modal-label">Verification code</div>
                <input className="hp-modal-input" data-testid="verify-code" type="text" placeholder="000000" maxLength={6} value={verifyCode} onChange={e => { setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setFormError(''); }} required style={{ textAlign: 'center', letterSpacing: '0.4em', fontFamily: 'var(--mono)', fontSize: '20px' }} />
              </div>
            </div>
            <button className="hp-modal-submit" data-testid="verify-submit-btn" type="submit" disabled={loading || verifyCode.length !== 6} style={{ marginTop: 14 }}>{loading ? 'Verifying...' : 'Verify & Create Account'}</button>
          </form>
          <div style={{ textAlign: 'center', fontSize: '12px', marginTop: 4 }}>
            <a style={{ color: 'var(--green)', cursor: 'pointer' }} onClick={handleResendCode}>Didn't receive the code? Resend</a>
          </div>
          <div className="hp-modal-footer"><a onClick={() => { setShowVerify(false); setShowCreate(true); }}>← Back to signup</a></div>
        </div>
      </div>

      {/* ── FORGOT PASSWORD ── */}
      <div className={`hp-modal-overlay ${showForgotPw ? 'open' : ''}`} onClick={(e) => e.target === e.currentTarget && setShowForgotPw(false)}>
        <div className="hp-modal">
          <div className="hp-modal-header">
            <div className="hp-modal-title">{forgotStep === 'email' ? 'Reset password' : 'Enter code'}</div>
            <button className="hp-modal-close" onClick={() => { setShowForgotPw(false); setForgotStep('email'); setFormError(''); }}>✕</button>
          </div>
          {forgotStep === 'email' ? (
            <form onSubmit={handleForgotSend}>
              <div className="hp-modal-fields">
                {formError && showForgotPw && <div className="hp-error">{formError}</div>}
                <div className="hp-modal-field">
                  <div className="hp-modal-label">Email</div>
                  <input className="hp-modal-input" type="email" placeholder="you@company.com" value={forgotEmail} onChange={e => { setForgotEmail(e.target.value); setFormError(''); }} required />
                </div>
              </div>
              <button className="hp-modal-submit" type="submit" disabled={loading} style={{ marginTop: 14 }}>{loading ? 'Sending...' : 'Send reset code'}</button>
            </form>
          ) : (
            <form onSubmit={handleForgotReset}>
              <div className="hp-modal-fields">
                {formError && showForgotPw && <div className="hp-error">{formError}</div>}
                <div className="hp-modal-field">
                  <div className="hp-modal-label">Verification code</div>
                  <input className="hp-modal-input" type="text" placeholder="000000" maxLength={6} value={forgotCode} onChange={e => { setForgotCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setFormError(''); }} required style={{ textAlign: 'center', letterSpacing: '0.4em', fontFamily: 'var(--mono)' }} />
                </div>
                <div className="hp-modal-field">
                  <div className="hp-modal-label">New password</div>
                  <input className="hp-modal-input" type="password" placeholder="••••••••" value={forgotNewPw} onChange={e => { setForgotNewPw(e.target.value); setFormError(''); }} required />
                </div>
                <div className="hp-modal-field">
                  <div className="hp-modal-label">Confirm new password</div>
                  <input className="hp-modal-input" type="password" placeholder="••••••••" value={forgotConfirmPw} onChange={e => { setForgotConfirmPw(e.target.value); setFormError(''); }} required />
                </div>
              </div>
              <button className="hp-modal-submit" type="submit" disabled={loading} style={{ marginTop: 14 }}>{loading ? 'Resetting...' : 'Reset password'}</button>
            </form>
          )}
          <div className="hp-modal-footer"><a onClick={() => { setShowForgotPw(false); setFormError(''); openSignin(); }}>← Back to sign in</a></div>
        </div>
      </div>

      {/* ── TERMS MODAL ── */}
      <div className={`hp-modal-overlay ${showTerms ? 'open' : ''}`} data-testid="terms-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowTerms(false)}>
        <div className="hp-modal hp-legal-modal">
          <div className="hp-modal-header">
            <div className="hp-modal-title">Terms &amp; Conditions</div>
            <button className="hp-modal-close" onClick={() => setShowTerms(false)}>✕</button>
          </div>
          <TermsContent />
          <button className="hp-modal-submit" onClick={() => { setAcceptTerms(true); setShowTerms(false); }}>I Agree to Terms &amp; Conditions</button>
        </div>
      </div>

      {/* ── PRIVACY MODAL ── */}
      <div className={`hp-modal-overlay ${showPrivacy ? 'open' : ''}`} data-testid="privacy-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowPrivacy(false)}>
        <div className="hp-modal hp-legal-modal">
          <div className="hp-modal-header">
            <div className="hp-modal-title">Privacy Policy</div>
            <button className="hp-modal-close" onClick={() => setShowPrivacy(false)}>✕</button>
          </div>
          <PrivacyContent />
          <button className="hp-modal-submit" onClick={() => { setAcceptPrivacy(true); setShowPrivacy(false); }}>I Agree to Privacy Policy</button>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
